<template>
    <div class="container-fluid px-2">
        <div class="">
            <!-- Error message -->
            <div v-if="error" class="alert alert-danger mb-4">
                {{ error }}
            </div>

            <div>
                <!-- Selfie Section -->
                <div class="mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <label class="form-label fw-bold">Live Selfie Verification</label>
                        <span v-if="agreement.selfie" class="badge bg-success rounded-3 px-3">
                            Completed
                        </span>
                    </div>

                    <div class="d-flex flex-column align-items-center">
                        <div v-if="!agreement.selfie" class="position-relative w-100" style="max-width: 500px">
                            <video ref="videoRef" autoplay playsinline muted class="w-100 rounded border"
                                :class="cameraOn ? 'd-block' : 'd-none'"
                                style="transform: scaleX(-1); max-height: 500px"></video>

                            <div v-if="!cameraOn"
                                class="w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-light rounded p-3">
                                <i class="fas fa-camera text-primary fs-1 mb-3"></i>
                                <button @click="startCamera" :disabled="loading" class="btn btn-primary px-4 py-2"
                                    style="min-width: 150px">
                                    <span v-if="loading">
                                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Loading...
                                    </span>
                                    <span v-else>Start Camera</span>
                                </button>
                                <p class="text-muted small mt-3 text-center">
                                    We'll verify your identity with a quick photo
                                </p>
                            </div>

                            <div v-if="cameraOn"
                                class="position-absolute bottom-0 start-0 end-0 p-3 bg-dark bg-opacity-50 rounded-bottom">
                                <button @click="takeSelfie" class="btn btn-primary w-100">
                                    Capture Photo
                                </button>
                            </div>
                        </div>

                        <div v-else class="position-relative">
                            <img :src="agreement.selfie" alt="Selfie Preview" class="img-thumbnail border-primary"
                                style="width: 160px; height: 160px; object-fit: cover">
                            <button @click="removeSelfie"
                                class="position-absolute top-0 end-0 translate-middle btn btn-danger btn-sm rounded-circle opacity-0"
                                style="transition: opacity 0.3s"
                                @mouseover="$event.target.classList.remove('opacity-0')"
                                @mouseleave="$event.target.classList.add('opacity-0')">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                    <canvas ref="canvasRef" class="d-none"></canvas>
                </div>

                <!-- ID Upload Section -->
                <div class="mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <label class="form-label fw-bold">Upload ID Documents</label>
                        <span class="text-muted small">
                            {{ agreement.uploadedId.length }}/2 uploaded
                        </span>
                    </div>

                    <div v-if="agreement.uploadedId.length < 2">
                        <input type="file" accept="image/*" multiple @change="onIdImagesChange" ref="fileInputRef"
                            class="d-none" :disabled="agreement.uploadedId.length >= 2">

                        <button @click="triggerFileInput" :disabled="agreement.uploadedId.length >= 2"
                            class="w-100 py-3 px-4 border-2 border-dashed rounded mb-3" :class="agreement.uploadedId.length >= 2
                                ? 'border-secondary text-secondary'
                                : 'border-primary text-primary'">
                            <div class="d-flex flex-column align-items-center">
                                <i class="fas fa-cloud-upload-alt fs-3 mb-2"></i>
                                <span>Click to upload ID documents</span>
                                <span class="text-muted small mt-1">
                                    Supported formats: JPG, PNG (max 5MB each)
                                </span>
                            </div>
                        </button>
                    </div>

                    <div class="row g-3 overflow-auto">
                        <div v-for="(src, idx) in agreement.uploadedId" :key="idx" class="col-md-6 position-relative">
                            <div class="card h-100">
                                <div class="card-body p-1 d-flex justify-content-center align-items-center bg-light"
                                    style="min-height: 160px">
                                    <img :src="src" :alt="`ID Preview ${idx + 1}`" class="img-fluid"
                                        style="max-height: 120px; object-fit: contain">
                                </div>
                                <button @click="removeIdImage(idx)"
                                    class="position-absolute top-0 end-0 translate-middle btn btn-danger btn-sm rounded-circle opacity-0"
                                    style="transition: opacity 0.3s"
                                    @mouseover="$event.target.classList.remove('opacity-0')"
                                    @mouseleave="$event.target.classList.add('opacity-0')">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineEmits, defineProps, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps({
    agreement: {
        type: Object,
        required: true,
        default: () => ({
            tos: false,
            signature: null,
            selfie: null,
            uploadedId: [],
            contract: null
        })
    }
})

const emit = defineEmits(['update:agreement'])

const cameraOn = ref(false)
const loading = ref(false)
const error = ref(null)
const videoRef = ref(null)
const canvasRef = ref(null)
const fileInputRef = ref(null)

const startCamera = async () => {
    error.value = null
    loading.value = true

    try {
        if (videoRef.value?.srcObject) {
            videoRef.value.srcObject.getTracks().forEach(track => track.stop())
        }

        const stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: 'user',
                width: { ideal: 1280 },
                height: { ideal: 720 }
            }
        })

        if (videoRef.value) {
            videoRef.value.srcObject = stream
            await new Promise(r => setTimeout(r, 100))
            await videoRef.value.play()
            cameraOn.value = true
        } else {
            error.value = 'Video element is not available.'
            cameraOn.value = false
        }
    } catch (err) {
        console.error('Camera error:', err)
        error.value = err.name === 'NotAllowedError'
            ? 'Camera permission denied. Please allow camera access.'
            : 'Could not access camera. Please check permissions and try again.'
        cameraOn.value = false
    } finally {
        loading.value = false
    }
}

const stopCamera = () => {
    if (videoRef.value?.srcObject) {
        videoRef.value.srcObject.getTracks().forEach(track => track.stop())
        cameraOn.value = false
    }
}

const takeSelfie = async () => {  // Added async here
    const video = videoRef.value
    const canvas = canvasRef.value

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
        error.value = 'Camera not ready. Please try again.'
        return
    }

    setTimeout(async () => {  // Added async here too
        try {
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const context = canvas.getContext('2d')
            context.drawImage(video, 0, 0, canvas.width, canvas.height)
            const dataUrl = await resizeImage(canvas, 200, 200)
            updateSelfie(dataUrl)
            stopCamera()
        } catch (err) {
            console.error('Error capturing selfie:', err)
            error.value = 'Failed to capture image: ' + err.message
        }
    }, 200)
}

const updateSelfie = (value) => {
    emit('update:agreement', {
        ...props.agreement,
        selfie: value
    })
}

const updateUploadedId = (value) => {
    emit('update:agreement', {
        ...props.agreement,
        uploadedId: value
    })
}

// Fix 1: Correct the resizeImage function to work with canvas
const resizeImage = (canvas, maxWidth, maxHeight) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = canvas.toDataURL('image/jpeg', 0.85);
        img.onload = () => {
            const resizedCanvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }
            }

            resizedCanvas.width = width;
            resizedCanvas.height = height;
            const ctx = resizedCanvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            resolve(resizedCanvas.toDataURL('image/jpeg', 0.85));
        };
    });
};

// Fix 2: Correct the onIdImagesChange function to properly handle async operations
const onIdImagesChange = async (e) => {  // Added async here
    error.value = null
    const files = Array.from(e.target.files).slice(0, 2)
    e.target.value = ''

    const validFiles = files.filter((file) => {
        if (!file.type.startsWith('image/')) {
            error.value = `File ${file.name} is not an image`
            return false
        }
        if (file.size > 5 * 1024 * 1024) {
            error.value = `File ${file.name} is too large (max 5MB)`
            return false
        }
        return true
    })

    // Process all files in parallel
    const images = await Promise.all(
        validFiles.map(async (file) => {
            return await resizeImageFile(file, 200, 200);  // Need a separate function for File objects
        })
    )

    updateUploadedId([
        ...props.agreement.uploadedId,
        ...images
    ].slice(0, 2))
}

// Add this new function to handle File objects
const resizeImageFile = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (event) => {
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                resolve(canvas.toDataURL('image/jpeg', 0.85));
            };
        };

        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};

const triggerFileInput = () => {
    fileInputRef.value?.click()
}

const removeSelfie = () => {
    updateSelfie(null)
}

const removeIdImage = (index) => {
    URL.revokeObjectURL(props.agreement.uploadedId[index])
    updateUploadedId(props.agreement.uploadedId.filter((_, i) => i !== index))
}

onMounted(() => {
    if (cameraOn.value && videoRef.value) {
        const video = videoRef.value
        if (video.srcObject) {
            video.play().catch(err => {
                console.error('Video play error:', err)
                error.value = 'Failed to start camera preview'
                cameraOn.value = false
            })
        }
    }
})

onUnmounted(() => {
    if (videoRef.value?.srcObject) {
        videoRef.value.srcObject.getTracks().forEach(track => track.stop())
    }
})
</script>

<style scoped>
:root {
    --white: #ffffff;
    --primary: #223698;
    --secondary: #e2e6ef;
    --accent: #02061b;
    --blue: #1f87fc;
    --mdb-primary: var(--primary);
}

.text-primary {
    color: var(--primary);
}
</style>